import { put, takeLatest, select } from 'redux-saga/effects';
import types from './types';
import actions from './actions';
import ktRequest from '../../../utils/request';
import AppSyncClient from '../../../appsync';
import { POST_ATTACHMENT_MESSAGE_TO_CHANNEL, MARK_MESSAGES_AS_READ_FOR_CHANNEL } from '../graphql/mutations';
import { GET_ALL_CHANNELS_AT_CENTER_AS_MANAGER } from '../graphql/queries';
import gql from 'graphql-tag';

const getCurrentCenterId = state => state.identity.centerId;
const getCurrentMessagesQuery = state => state.messages.managerOverview.currentQuery;
const getNextTokens = state => state.messages.managerOverview.nextTokens;

function * uploadAttachment (payload) {
  try {
    const { file, message, channelId } = payload;
    const { name, type } = file;

    const body = {
      fileName: name,
      fileType: type
    };
    const presignedUrlResponse = yield ktRequest.post('presigned', body, 'messaging');

    if (!presignedUrlResponse.ok) {
      throw new Error('File upload error');
    }

    const json = yield presignedUrlResponse.json();
    const { data } = json;
    const { fields: { Key: attachmentKey }, url } = data;
    let formData = new FormData();

    Object.keys(data.fields).forEach(key => formData.append(key, data.fields[key]));
    formData.append('file', file);

    const presignedPostResponse = yield fetch(url, {
      method: 'POST',
      body: formData
    });

    if (!presignedPostResponse.ok) {
      throw new Error('Unable to upload attachment');
    }

    yield AppSyncClient.mutate({
      mutation: POST_ATTACHMENT_MESSAGE_TO_CHANNEL,
      variables: {
        channelId,
        message,
        attachmentS3Key: attachmentKey
      }
    });

    yield put(actions.uploadAttachmentToChannelSuccess());
  } catch (error) {
    yield put(actions.uploadAttachmentToChannelFailure(error));
  }
}

function * managerOverviewQueryRequest (payload) {
  try {
    const { query } = payload;
    const currentQueryParams = yield select(getCurrentMessagesQuery);
    // if no details are provided use the ones stored in redux
    const queryParams = query || currentQueryParams;
    const { channelIds, ...otherQueryParams } = queryParams;

    let channelIdsToQuery = channelIds;
    // no ids provided, query for all channels
    if (channelIdsToQuery.length === 0) {
      channelIdsToQuery = yield getAllChannelIds();
    }

    const nextTokens = yield select(getNextTokens);
    const queryingWithNextTokens = Object.keys(nextTokens).length > 0;

    /**
     * Instead of making N amount of requests for N amount of channels, we're combining N channels into 1 request
     * We use our query paramters and requested channel ids and create "one" query
     */

    const singleQueryString = id => `getAllMessagesAsManager(query: {
      channelId: "${id}"
      senderId: ${otherQueryParams.senderId ? `"${otherQueryParams.senderId}"` : null}
      startDate: "${otherQueryParams.startDate}"
      endDate: "${otherQueryParams.endDate}"
      nextToken: ${nextTokens[id] ? `"${nextTokens[id]}"` : null}
    }) {
      messages {
        id
        channelId
        senderId
        message
        createdAt
        user {
          id
          name
        }
        channel {
          name
          directMessage
          private
          broadcastType
        }
      }
      nextToken
    }`;

    // dictionary to keep track which channelId is associated with the randomly created one. used to associated a nextToken (if returned) with the channel
    const gqlChannelsMap = {};
    // iterate over the requested channel ids and insert the id into the query string function, then join all of the string together
    const combinedQueryString = channelIdsToQuery.map((id, index) => {
      // need this check so we don't refetch channels that don't have any additional messages
      if (queryingWithNextTokens) {
        if (nextTokens[id]) {
          gqlChannelsMap[`channel${index}`] = id;
          return `channel${index}: ${singleQueryString(id)}`;
        } else {
          return null;
        }
      } else {
        gqlChannelsMap[`channel${index}`] = id;
        return `channel${index}: ${singleQueryString(id)}`;
      }
    }).filter(str => str !== null).join('\n');

    const gqlQuery = gql`query { ${combinedQueryString} }`;

    const response = yield AppSyncClient.query({
      query: gqlQuery,
      fetchPolicy: 'network-only'
    });

    let combinedMessages = [];
    let newNextTokens = {};

    if (response.data) {
      /**
       * `response.data` is an object where the key is `channel[index]` which will contain two properties: `messages` and `nextToken`
       */
      Object.keys(response.data).forEach(key => {
        const value = response.data[key];

        if (value.messages) {
          combinedMessages = combinedMessages.concat(value.messages);
        }

        if (value.nextToken) {
          newNextTokens[gqlChannelsMap[key]] = value.nextToken;
        }
      });
    }

    combinedMessages.sort((m1, m2) => new Date(m2.createdAt) - new Date(m1.createdAt));

    yield put(actions.managerMessagesQuerySuccess(combinedMessages, newNextTokens));
  } catch (error) {
    yield put(actions.managerMessagesQueryFailure(error));
  }
}

function * getAllChannelIds () {
  try {
    const currentCenterId = yield select(getCurrentCenterId);
    const response = yield AppSyncClient.query({
      fetchPolicy: 'cache-first',
      query: GET_ALL_CHANNELS_AT_CENTER_AS_MANAGER,
      variables: {
        centerId: currentCenterId
      }
    });

    return response.data.getAllChannelsAtCenterAsManager.map(channel => channel.id);
  } catch (error) {
    console.log(error);
    return [];
  }
}

function * selectChannel (payload) {
  try {
    const { channel: { id: channelId } } = payload;

    if (channelId) {
      yield AppSyncClient.mutate({
        mutation: MARK_MESSAGES_AS_READ_FOR_CHANNEL,
        variables: {
          channelId
        }
      });
    }
  } catch (error) {
    console.log(error);
  }
}

export const channelMessageSagas = [
  takeLatest(types.UPLOAD_ATTACHMENT_TO_CHANNEL_REQUEST, uploadAttachment),
  takeLatest(types.MANAGER_MESSAGES_OVERVIEW_REQUEST, managerOverviewQueryRequest),
  takeLatest(types.SELECT_CHANNEL, selectChannel)
];
